import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, HStack, Stack, Button, useToast } from "@chakra-ui/react";
import { getArchiveData, updateArchiveData } from "../../../services/Archive";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import TypeaheadWidget from "../../../CommonComponents/TypeaheadWidget";
import { getResearchCategoriesAPI } from "../../../services/Films";
import FormLoader from "../../../CommonComponents/FormLoader";
import { useSelector } from "react-redux";
import {
  handleApiError,
  showErrorToast,
  showSuccessToast,
} from "../../../util/errorHandler";
import {
  getChangedResearchCategorySlots,
  syncResearchCategorySlots,
  toResearchCategoryValueMap,
} from "../../../util/researchCategorySync";

// Renders nothing; keeps the field value in formData (ui:widget "hidden" does not apply to array fields)
const HiddenField = () => null;

const ResearchCategories = () => {
  const { alias } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const toast = useToast();
  // Record as loaded from the API, used to detect which research category slots the admin changed
  const originalDataRef = useRef({});
  // Research categories list (each entry has both `alias` and `value`), fetched once
  const researchCategoriesRef = useRef(null);

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = [
      "_id",
      "research_categories",
      "research_category1",
      "research_category2",
      "research_category3",
      "research_category4",
      "research_category1_alias",
      "research_category2_alias",
      "research_category3_alias",
      "research_category4_alias",
    ];
    const rightFields = [
      "additional_research_categories",
      "additional_research_categories_alias",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });

    return { leftData, rightData };
  };

  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    title: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      research_categories: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Research Categories",
      },
      research_category1: {
        type: "string",
        title: "Research Category 1",
      },
      research_category2: {
        type: "string",
        title: "Research Category 2",
      },
      research_category3: {
        type: "string",
        title: "Research Category 3",
      },
      research_category4: {
        type: "string",
        title: "Research Category 4",
      },
      research_category1_alias: {
        type: "string",
        title: "Research Category 1 Alias",
      },
      research_category2_alias: {
        type: "string",
        title: "Research Category 2 Alias",
      },
      research_category3_alias: {
        type: "string",
        title: "Research Category 3 Alias",
      },
      research_category4_alias: {
        type: "string",
        title: "Research Category 4 Alias",
      },
    },
  };

  // Right column schema
  const rightColumnSchema = {
    title: "",
    type: "object",
    properties: {
      additional_research_categories: {
        type: "string",
        title: "Additional Research Categories",
      },
      additional_research_categories_alias: {
        type: "string",
        title: "Additional Research Categories Alias",
      },
    },
  };

  // Combined schema for preview mode
  const combinedSchema = {
    title: "Research Categories Fields",
    description: "",
    type: "object",
    properties: {
      _id: { type: "string" },
      research_categories: {
        type: "array",
        items: {
          type: "string",
        },
        title: "Research Categories",
      },
      research_category1: {
        type: "string",
        title: "Research Category 1",
      },
      research_category2: {
        type: "string",
        title: "Research Category 2",
      },
      research_category3: {
        type: "string",
        title: "Research Category 3",
      },
      research_category4: {
        type: "string",
        title: "Research Category 4",
      },
      research_category1_alias: {
        type: "string",
        title: "Research Category 1 Alias",
      },
      research_category2_alias: {
        type: "string",
        title: "Research Category 2 Alias",
      },
      research_category3_alias: {
        type: "string",
        title: "Research Category 3 Alias",
      },
      research_category4_alias: {
        type: "string",
        title: "Research Category 4 Alias",
      },
      additional_research_categories: {
        type: "string",
        title: "Additional Research Categories",
      },
      additional_research_categories_alias: {
        type: "string",
        title: "Additional Research Categories Alias",
      },
    },
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    research_category1: { "ui:widget": "hidden" },
    research_category2: { "ui:widget": "hidden" },
    research_category3: { "ui:widget": "hidden" },
    research_category4: { "ui:widget": "hidden" },
    research_category1_alias: {
      "ui:widget": "typeahead",
      "ui:emptyValue": "",
    },
    research_category2_alias: {
      "ui:widget": "typeahead",
      "ui:emptyValue": "",
    },
    research_category3_alias: {
      "ui:widget": "typeahead",
      "ui:emptyValue": "",
    },
    research_category4_alias: {
      "ui:widget": "typeahead",
      "ui:emptyValue": "",
    },
    research_categories: { "ui:field": HiddenField },
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    "ui:style": {
      fontSize: "14px",
    },
    additional_research_categories: {
      "ui:widget": "typeahead1",
      "ui:emptyValue": "",
    },
    additional_research_categories_alias: {
      "ui:widget": "typeahead",
      "ui:emptyValue": "",
    },
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { norender: true },
    _id: { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    research_category1_alias: {
      "ui:widget": "typeahead",
      "ui:emptyValue": "",
    },
    research_category2_alias: {
      "ui:widget": "typeahead",
      "ui:emptyValue": "",
    },
    research_category3_alias: {
      "ui:widget": "typeahead",
      "ui:emptyValue": "",
    },
    research_category4_alias: {
      "ui:widget": "typeahead",
      "ui:emptyValue": "",
    },
    additional_research_categories_alias: {
      "ui:widget": "typeahead",
      "ui:emptyValue": "",
    },
    research_category1: { "ui:widget": "hidden" },
    research_category2: { "ui:widget": "hidden" },
    research_category3: { "ui:widget": "hidden" },
    research_category4: { "ui:widget": "hidden" },
    additional_research_categories: {
      "ui:widget": "typeahead1",
      "ui:emptyValue": "",
    },
    research_categories: { "ui:field": HiddenField },
  };

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={(inputValue) => fetchCategories(inputValue, "alias")}
      />
    ),
    typeahead1: (props) => (
      <TypeaheadWidget
        {...props}
        fetchOptions={(inputValue) => fetchCategories(inputValue, "value")}
      />
    ),
  };

  const transformOptions = (data, keyForLabelValue = "alias") => {
    return data.map((itemArray) => {
      // Convert the array of key-value pairs into a single object
      const entity = itemArray.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {});

      // Use the `alias` field for label and value
      return {
        label: entity[keyForLabelValue],
        value: entity[keyForLabelValue],
      };
    });
  };

  const getResearchCategories = async () => {
    if (!researchCategoriesRef.current) {
      researchCategoriesRef.current = (await getResearchCategoriesAPI()) || [];
    }
    return researchCategoriesRef.current;
  };

  const fetchCategories = async (inputValue, keyForLabelValue = "alias") => {
    const result = await getResearchCategories();
    const transformedOptions = transformOptions(result, keyForLabelValue);

    // This function return the options
    return transformedOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  // The four research_categoryN_alias dropdowns are the source of truth: derive research_categoryN
  // from the changed alias slots and rebuild research_categories, which also keeps
  // additional_research_categories as the backend does (see util/researchCategorySync).
  // Returns null if a selected alias has no matching research category.
  const syncResearchCategories = async (formData) => {
    const original = originalDataRef.current || {};
    if (getChangedResearchCategorySlots(formData, original).length === 0) {
      return formData;
    }
    const { data, missingAlias } = syncResearchCategorySlots(
      formData,
      original,
      toResearchCategoryValueMap(await getResearchCategories()),
      { extraValueFields: ["additional_research_categories"] },
    );
    if (missingAlias) {
      showErrorToast(
        toast,
        `Research category "${missingAlias}" was not found. Please select it again from the list.`,
      );
      return null;
    }
    return data;
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getArchiveData(alias, "research-categories");
          if (result) {
            const data = result?.data?.[0] || {};
            originalDataRef.current = data;

            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          originalDataRef.current = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      let finalFormData;

      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }

      finalFormData = await syncResearchCategories(finalFormData);
      if (!finalFormData) {
        return;
      }

      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateArchiveData(alias, finalFormData);

        if (result?.status === 200) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">
                    Archive Research Categories Fields
                  </Box>
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  widgets={widgets}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection="row"
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">
                        Archive Research Categories Fields
                      </Box>
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ResearchCategories;
